import { Platform } from 'react-native';

import { HAPTICS_ENABLED_BY_DEFAULT, readHapticsEnabled } from './hapticsPrefStorage';

/**
 * Тактильная отдача (стори 3.1, SND-1).
 *
 * До этого в приложении не было ни одного обращения к Haptics или Vibration — для мессенджера
 * 2026 года это самый заметный разрыв с конкурентами: отправка сообщения, старт записи, приём
 * звонка и long-press ожидаемо отдают в палец.
 *
 * Обёртка узкая и молчаливая. Тактильная отдача необязательна: она не должна ни ронять сценарий,
 * ни требовать `await` у вызывающего. Поэтому наружу не выходит ни одно исключение, а сами
 * функции синхронные — вызывающий пишет `hapticLight()` посреди обработчика и не думает о
 * промисах, которые некому ждать.
 */

type ImpactLevel = 'light' | 'medium' | 'heavy';
type NotificationLevel = 'success' | 'warning' | 'error';

type HapticsModule = {
  impactAsync?: (style: unknown) => Promise<void>;
  notificationAsync?: (type: unknown) => Promise<void>;
  selectionAsync?: () => Promise<void>;
  ImpactFeedbackStyle?: Record<string, unknown>;
  NotificationFeedbackType?: Record<string, unknown>;
};

/**
 * Модуль нативный, и сборка, сделанная до его добавления, обязана продолжать работать —
 * тот же приём, что у видеокомпрессора (`loadVideoCompressor`).
 */
function loadHaptics(): HapticsModule | null {
  if (Platform.OS === 'web') {
    return null;
  }
  try {
    return (require('expo-haptics') as HapticsModule) ?? null;
  } catch {
    return null;
  }
}

let cachedModule: HapticsModule | null | undefined;

function haptics(): HapticsModule | null {
  if (cachedModule === undefined) {
    cachedModule = loadHaptics();
  }
  return cachedModule;
}

/**
 * Настройка читается из хранилища асинхронно, а отдача нужна синхронно, в момент нажатия.
 * Держим последнее известное значение в памяти: до первого чтения действует умолчание, дальше —
 * то, что выбрал человек. Промахнуться можно только один раз и только до `primeHapticsPref()`.
 */
let enabled = HAPTICS_ENABLED_BY_DEFAULT;

/** Прочитать сохранённую настройку в память. Вызывается один раз при старте приложения. */
export async function primeHapticsPref(): Promise<void> {
  try {
    enabled = await readHapticsEnabled();
  } catch {
    enabled = HAPTICS_ENABLED_BY_DEFAULT;
  }
}

/** Применить изменение настройки немедленно, не дожидаясь записи в хранилище. */
export function setHapticsEnabled(next: boolean): void {
  enabled = next;
}

export function isHapticsEnabled(): boolean {
  return enabled;
}

function fire(action: (mod: HapticsModule) => Promise<void> | undefined): void {
  if (!enabled) return;
  const mod = haptics();
  if (!mod) return;
  try {
    // Промис никто не ждёт — и не должен: отклик уже произошёл или не произошёл, а отказ
    // системы (например, выключенная в ОС отдача) — это не ошибка сценария.
    void action(mod)?.catch(() => {});
  } catch {
    /* тактильная отдача не имеет права ломать то, ради чего её вызвали */
  }
}

function impact(level: ImpactLevel): void {
  fire((mod) => mod.impactAsync?.(mod.ImpactFeedbackStyle?.[level === 'light' ? 'Light' : level === 'medium' ? 'Medium' : 'Heavy'] ?? level));
}

function notify(level: NotificationLevel): void {
  fire((mod) =>
    mod.notificationAsync?.(
      mod.NotificationFeedbackType?.[
        level === 'success' ? 'Success' : level === 'warning' ? 'Warning' : 'Error'
      ] ?? level,
    ),
  );
}

/** Рутинное действие: отправка сообщения, остановка записи. */
export function hapticLight(): void {
  impact('light');
}

/** Переключение состояния: старт записи голосового, включение режима. */
export function hapticMedium(): void {
  impact('medium');
}

/** Успешное завершение: вложение доехало. */
export function hapticSuccess(): void {
  notify('success');
}

/** Отказ: вложение не отправилось, звонок сорвался. */
export function hapticError(): void {
  notify('error');
}

/** Выбор из списка и long-press: самый лёгкий из откликов. */
export function hapticSelection(): void {
  fire((mod) => mod.selectionAsync?.());
}
